/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useCurrencies — the platform currency catalog, fetched once.
 *
 * Separate from useEventLookups because it is not a lookup table: currencies
 * are an Arcee model behind /arcee/payments/currencies/, not an
 * ApplicationTable behind the generic fetch endpoint. See currencyCatalog.js.
 *
 * Cached hard on purpose. This is a handful of rows seeded by whoever operates
 * the platform, and it changes roughly never — but it is read once per price
 * rendered on an event page, so a short staleTime would turn "show the code
 * beside the fee" into a request per card.
 *
 * FAILS TO EMPTY, NEVER TO BLOCKING. A catalog that will not load leaves
 * prices rendering as bare numbers (formatAmount drops the code when it has no
 * currency) rather than hiding them. On the organizer form the picker is empty
 * and validation blocks the save, which is the same treatment useEventLookups
 * gives a lookup table that fails — the organizer keeps filling in everything
 * else instead of facing a blank screen.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { readCurrencies, EMPTY_CATALOG } from '../components/athlohive/currencyCatalog';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export const CURRENCIES_ENDPOINT = '/arcee/payments/currencies/';

export default function useCurrencies() {
  const query = useQuery({
    // No organization in the key: the catalog is platform-wide, so one cache
    // entry serves every org this browser visits.
    queryKey: ['currencies'],
    queryFn: async ({ signal }) => {
      // publicApiCall, not apiCall: the event detail page is public, and a
      // visitor who is not signed in still has to see the currency beside the
      // entry fee. A session-bound call would 403 for exactly the audience
      // deciding whether to pay. The endpoint is IsApplicationAuthenticated,
      // so the client credentials these headers carry are what it checks.
      const resp = await apiHelper.publicApiCall(CURRENCIES_ENDPOINT, { method: 'GET', signal });
      if (!resp.ok) throw new Error(`currencies: ${resp.status}`);
      return readCurrencies(await resp.json());
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });

  const catalog = useMemo(() => query.data || EMPTY_CATALOG, [query.data]);

  return {
    catalog,
    isLoading: query.isLoading,
    isError: query.isError,
    // Loaded fine and has nothing in it — a platform that has not seeded a
    // single Currency row. Reported apart from isError for the reason
    // useEventLookups separates the two: one says "try again", the other says
    // "ask an administrator".
    isEmpty: !query.isLoading && !query.isError && catalog.list.length === 0,
    refetch: query.refetch,
  };
}
